import { fixedUi } from '@/i18n/fixedUi';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { chartText } from '@/i18n/chartText';
import RequiredMark from '@/temu-platform/components/RequiredMark';
import {
  TEMU_VEHICLE_FITMENT_SEE_DETAILS,
  TEMU_VEHICLE_FITMENT_UNIVERSAL,
  resolveTemuVehicleFitmentMode,
} from '@/temu-platform/utils/temuVehicleLibraryRequired';

function parseIdList(text) {
  return String(text || '')
    .split(/[,，\s;；]+/)
    .map((s) => String(s || '').trim())
    .filter(Boolean)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function idsToText(ids) {
  return (Array.isArray(ids) ? ids : [])
    .map((id) => Number(id))
    .filter((n) => Number.isFinite(n) && n > 0)
    .join(', ');
}

/**
 * 车型库兼容数据（公告：适用车型=见详情时必填）。
 * 完整车型树选车器尚未接入 OpenAPI，先支持末级 dependencyId / K-type / relationId 录入。
 */
export default function TemuVehicleFitmentPanel({
  visible = true,
  required = false,
  attrTpl = null,
  productAttributes = {},
  value = null,
  onChange,
  errorMessage = '',
}) {
  useTranslation('workbench');
  const rel = value && typeof value === 'object' ? value : {};
  const [depDraft, setDepDraft] = useState(() => idsToText(rel.leafPropertyValueDependencyIdList));
  const [ktypeDraft, setKtypeDraft] = useState(() => idsToText(rel.ktype));

  const fitmentMode = useMemo(
    () => resolveTemuVehicleFitmentMode({ attrTpl, productAttributes }),
    [attrTpl, productAttributes],
  );

  if (!visible) return null;

  const commitDeps = (text) => {
    setDepDraft(text);
    onChange?.({
      ...rel,
      relationType: Number(rel.relationType || 1) || 1,
      leafPropertyValueDependencyIdList: parseIdList(text),
    });
  };

  const commitKtypes = (text) => {
    setKtypeDraft(text);
    onChange?.({
      ...rel,
      relationType: Number(rel.relationType || 1) || 1,
      ktype: parseIdList(text),
    });
  };

  const commitRelationId = (raw) => {
    const n = Number(String(raw || '').trim());
    onChange?.({
      ...rel,
      relationType: Number(rel.relationType || 1) || 1,
      relationId: Number.isFinite(n) && n > 0 ? n : undefined,
    });
  };

  return (
    <div className="tpo-vehicle-fitment" data-field-error="goodsVehiclePropertyRelation">
      <div className="tpo-size-chart__title-row">
        <h4 className="tpo-publish__subhead">
          {chartText('publishExtra.vehicleTitle', '兼容车型')}
          {required ? <RequiredMark /> : null}
        </h4>
      </div>

      {fitmentMode === TEMU_VEHICLE_FITMENT_UNIVERSAL ? (
        <p className="tpo-wizard__inline-hint">
          {chartText('publishExtra.vehicleUniversal', '当前「适用车型」为通用，车型库非必填；提交时不会校验兼容车型数据。')}
        </p>
      ) : null}

      {fitmentMode === TEMU_VEHICLE_FITMENT_SEE_DETAILS ? (
        <p className="tpo-wizard__inline-hint">
          {chartText('publishExtra.vehicleDetails', '当前「适用车型」为见详情，请填写兼容车型数据（末级 dependencyId / K-type / 车型库 relationId 至少一项）。')}
        </p>
      ) : null}

      {fitmentMode !== TEMU_VEHICLE_FITMENT_SEE_DETAILS
        && fitmentMode !== TEMU_VEHICLE_FITMENT_UNIVERSAL ? (
          <p className="tpo-wizard__inline-hint">
            {chartText('publishExtra.vehiclePick', '请先在类目属性中选择「适用车型 / 适用车型维护」。选「见详情」后需填写兼容车型；选「通用」则非必填。')}
          </p>
        ) : null}

      {errorMessage ? <div className="tpo-field__error" role="alert">{errorMessage}</div> : null}

      <div className="tpo-wizard__form">
        <label className="tpo-field tpo-field--full">
          <span className="tpo-field__label">{chartText('publishExtra.depIds', fixedUi("末级属性 dependencyId 列表"))}</span>
          <input
            className="tpo-input"
            value={depDraft}
            onChange={(e) => commitDeps(e.target.value)}
            placeholder={chartText('publishExtra.depPh', '多个 ID 用逗号分隔，如 1001,1002')}
          />
        </label>
        <label className="tpo-field tpo-field--full">
          <span className="tpo-field__label">{chartText('publishExtra.ktypes', fixedUi("K-type 列表"))}</span>
          <input
            className="tpo-input"
            value={ktypeDraft}
            onChange={(e) => commitKtypes(e.target.value)}
            placeholder={chartText('publishExtra.ktypePh', '可选；多个用逗号分隔')}
          />
        </label>
        <label className="tpo-field">
          <span className="tpo-field__label">{chartText('publishExtra.relationId', fixedUi("车型库 relationId"))}</span>
          <input
            className="tpo-input"
            value={rel.relationId != null && Number(rel.relationId) > 0 ? String(rel.relationId) : ''}
            inputMode="numeric"
            onChange={(e) => commitRelationId(e.target.value)}
            placeholder={chartText('publishExtra.relationPh', '可选；引用已有兼容车型库')}
          />
        </label>
      </div>
    </div>
  );
}
